import { calculateEAC, type EacMethod } from '@/lib/utils';
import type { HoursBurndownEac } from '../types';
import { getEacChartLabel } from './eac-method-labels';
import { MOCK_EAC_HOURS_HTC, MOCK_HTC_HOURS } from './forecast-mocks';

export interface HoursBurndownEacInputs {
  actualHours: number;
  budgetHours: number;
  completionPercent: number;
  /** When on, HTC hours are passed as estimateToComplete for the Re-estimate path */
  showHoursToComplete: boolean;
  /** Whether the PM has submitted Hours to Complete data */
  htcAvailable: boolean;
  method: EacMethod;
}

/**
 * EAC hours for the Hours Burndown chart — the same EAC math as costs, with hours as the unit.
 */
export function calculateHoursBurndownEac({
  actualHours,
  budgetHours,
  completionPercent,
  showHoursToComplete,
  htcAvailable,
  method,
}: HoursBurndownEacInputs): HoursBurndownEac {
  const result = completionPercent > 0 && actualHours > 0 && budgetHours > 0
    ? calculateEAC(
        {
          actualCosts: actualHours,
          totalBudget: budgetHours,
          completionPercent,
          estimateToComplete: showHoursToComplete ? MOCK_HTC_HOURS : 0,
        },
        method,
      )
    : null;

  const activeMethod = result?.method ?? method;

  return {
    eacTotalHours: result?.eac ?? null,
    eacTotalHoursHtc: htcAvailable ? MOCK_EAC_HOURS_HTC : null,
    activeMethod,
    eacMethodLabel: getEacChartLabel(activeMethod),
  };
}

/**
 * Remaining hours at a month offset, projected from an EAC total using the same
 * S-curve shape as the planned line.
 */
export function eacRemainingAt(eacTotal: number, monthIndex: number, totalMonths: number): number {
  const t = totalMonths > 0 ? monthIndex / totalMonths : 1;
  const consumed = eacTotal * (3 * t * t - 2 * t * t * t);
  return Math.max(0, eacTotal - consumed);
}
